import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { Box, Container, Divider, Typography, useTheme, useMediaQuery } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AppPage from '../../../shared/components/PageComponents/AppPage';
import GamesFormV2, { DEFAULT_VALUES, FormValues as GamesFormValues } from './GamesFormV2';
import 'react-perfect-scrollbar/dist/css/styles.css';
import GamesList from './GameList';
import GamesCalendarToolbar from './GamesCalendarToolbar';
import { useAuth } from '@/features/scaffolding/components/AuthContext';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { usePersistedValue } from '../hooks/usePersistedValue';
import { useGameDates } from '../hooks/gameQueries';
import { dateFromDayKey, localDayKey } from '../services/seasonDates';
import { Access } from '@/types/user';

type ViewMode = 'cards' | 'list' | 'calendar';

const GamesPage = () => {
  const [formValues, setFormValues] = useState<GamesFormValues>(DEFAULT_VALUES);
  const [containerHeight, setContainerHeight] = useState(0);
  const [viewMode, setViewMode] = usePersistedValue<ViewMode>('games-view-mode', 'cards');
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { currentOrg, currentLeague } = useUserStateContext();

  const boxRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const teamId = formValues.team ? parseInt(formValues.team, 10) : undefined;
  const seasonId = formValues.seasonId ? parseInt(formValues.seasonId, 10) : undefined;

  // Determine if the org has single-team access (calendar view only available then)
  const singleTeamAccess = useMemo(() => {
    // access lives on the runtime object even though the TS type doesn't declare it
    const access = (currentOrg as any)?.access as Access[] | undefined;
    if (!access) return null;
    const teamEntries = access.filter((a) => a.teamId != null);
    if (teamEntries.length === 1) return teamEntries[0];
    return null;
  }, [currentOrg]);

  const focusTeamId = singleTeamAccess?.teamId ?? undefined;

  // Fall back to cards when calendar is unavailable or toggle is hidden
  useEffect(() => {
    if (viewMode === 'calendar' && !focusTeamId) {
      setViewMode('cards');
    }
  }, [focusTeamId]);

  // Memoize the callback to prevent infinite re-renders
  const handleFilterAndSortChange = useCallback((values: GamesFormValues) => {
    setFormValues(values);
  }, []);

  const handleCalendarMonthChange = useCallback((date: Date) => {
    setCalendarMonth(date);
  }, []);

  // GamesFormV2 isn't mounted in calendar mode, so the toolbar's season select
  // writes here directly. Stable identity: ListSeasons re-runs its auto-select
  // effect whenever onChange changes.
  const handleCalendarSeasonChange = useCallback((nextSeasonId: string) => {
    setFormValues((prev) => ({ ...prev, seasonId: nextSeasonId }));
  }, []);

  // Snap the calendar to today, clamped to the season's first and last game
  // dates: the current month while the season is underway, its last month once
  // it's over, its first month before it starts. The same dates feed the
  // calendar toolbar's month menu and prev/next bounds.
  const { data: gameDatesData } = useGameDates({
    seasonId,
    teamId: focusTeamId,
    streamTypes: formValues.streamType,
  });
  const centeredSeasonRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!seasonId) return;
    if (centeredSeasonRef.current === seasonId) return;
    const dates = gameDatesData?.dates;
    if (!dates?.length) return;

    // Dates come back as "yyyy-MM-dd" — lexically sortable, so the clamp is a
    // plain string compare. dateFromDayKey parses in local time so westward
    // timezones don't shift it to the previous day.
    const todayKey = localDayKey(new Date());
    const first = dates.reduce((a, b) => (a < b ? a : b));
    const last = dates.reduce((a, b) => (a > b ? a : b));
    const target = dateFromDayKey(todayKey < first ? first : todayKey > last ? last : todayKey);
    if (!target) return;

    setCalendarMonth(target);
    centeredSeasonRef.current = seasonId;
  }, [seasonId, gameDatesData?.dates]);

  // For calendar mode, use the 15th of the calendar month as the reference date
  const effectiveDate = useMemo(() => {
    if (viewMode === 'calendar') {
      return new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 15);
    }
    if (!formValues.date) return undefined;
    try {
      const dateObj = formValues.date instanceof Date ? formValues.date : new Date(formValues.date);
      return !isNaN(dateObj.getTime()) ? dateObj : undefined;
    } catch (error) {
      return undefined;
    }
  }, [viewMode, calendarMonth, formValues.date]);

  const { sessionState } = useAuth();

  // Calculate container height more efficiently
  const calculateContainerHeight = useCallback(() => {
    if (!boxRef.current || !containerRef.current) return;

    const boxTop = boxRef.current.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;

    // Account for padding at the bottom
    const bottomPadding = 24; // 24px

    // Calculate the available height
    const availableHeight = windowHeight - boxTop - bottomPadding;

    // Set a minimum height
    const minHeight = 300;
    const newHeight = Math.max(availableHeight, minHeight);

    setContainerHeight(newHeight);
  }, []);

  // Update height on mount and window resize
  useEffect(() => {
    calculateContainerHeight();

    const handleResize = () => {
      calculateContainerHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateContainerHeight]);

  // Update height when form values or view mode change
  useEffect(() => {
    // Small delay to allow form to render
    const timer = setTimeout(() => {
      calculateContainerHeight();
    }, 100);

    return () => clearTimeout(timer);
  }, [formValues, viewMode, calculateContainerHeight]);

  const title = 'Games';
  useDocumentTitle(title);

  return (
    <AppPage title={title} requiredPermissions={['menu:games']}>

      <Container ref={containerRef} maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Select a game</Typography>
            {<ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => { if (value) setViewMode(value); }}
              size="medium"
              sx={{
                gap: 0.5,
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid transparent',
                  borderRadius: '2px !important',
                  bgcolor: 'transparent',
                  color: 'text.primary',
                  opacity: 0.6,
                  px: 2,
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {  opacity: 1, },
                  '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black' ,
                    opacity: 1,
                    '&:hover': { opacity: 0.5, bgcolor: `rgba(255,255,255,0.8)`},
                  },
                },
              }}
            >
              <ToggleButton value="cards" aria-label="Card view">
                Cards
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                List
              </ToggleButton>
              {focusTeamId && (
                <ToggleButton value="calendar" aria-label="Calendar view">
                  Calendar
                </ToggleButton>
              )}
            </ToggleButtonGroup>}
          </Box>
          <Divider sx={{ marginTop: 3, marginBottom: 2 }} />

          {viewMode !== 'calendar' && (
           <GamesFormV2 onChange={handleFilterAndSortChange} />
          )}
          {viewMode === 'calendar' && (
            <GamesCalendarToolbar
              leagueId={currentLeague?.id}
              seasonId={formValues.seasonId}
              onSeasonChange={handleCalendarSeasonChange}
              currentMonth={calendarMonth}
              onMonthChange={handleCalendarMonthChange}
              gameDates={gameDatesData?.dates}
            />
          )}

        <Box
          ref={boxRef}
          sx={{
            ...(viewMode === 'calendar'
              ? { overflow: 'visible' }
              : {
                  height: isSmallScreen ? 'auto' : `${containerHeight}px`,
                  maxHeight: isSmallScreen ? 'none' : 'calc(100vh - 300px)',
                  overflow: 'auto',
                }),
            mt: 2,
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'rgba(177,177,177,0)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(177, 177, 177, 0.72)',
              borderRadius: '4px',
            },
          }}
        >
          <GamesList
            key={`${currentLeague?.id ?? 'no-league'}-${teamId || 'all'}-${seasonId || 'all'}-${effectiveDate?.toISOString() || 'no-date'}-${formValues.streamType.join('-')}-${viewMode}`}
            teamId={viewMode === 'calendar' ? focusTeamId : teamId}
            seasonId={seasonId}
            date={effectiveDate}
            streamTypes={formValues.streamType}
            containerRef={boxRef}
            viewMode={viewMode}
            focusTeamId={focusTeamId}
            calendarMonth={calendarMonth}
          />
        </Box>
      </Container>
    </AppPage>
  );
};

export default GamesPage;
